import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    Divider,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    Card,
    CardContent,
    CardHeader,
    useTheme,
    alpha
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Percent as PercentIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Receipt as ReceiptIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { settingsApi } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const SystemSettingsPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [taxRates, setTaxRates] = useState({
        cosmeticsTax: 0.15,
        generalTax: 0.00
    });

    const [companyInfo, setCompanyInfo] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        slogan: ''
    });

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const [taxResponse, companyResponse] = await Promise.all([
                settingsApi.getTax(),
                settingsApi.getCompany()
            ]);

            if (taxResponse.data.success) {
                setTaxRates({
                    cosmeticsTax: taxResponse.data.taxRates.Cosmetics,
                    generalTax: taxResponse.data.taxRates.general
                });
            }

            if (companyResponse.data.success) {
                setCompanyInfo(companyResponse.data.companyInfo);
            }
        } catch (error) {
            console.error("Failed to fetch settings:", error);
            toast.error(t('settings.failedToLoadSettings'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleSaveCompany = async () => {
        try {
            setSaving(true);
            const response = await settingsApi.updateCompany(companyInfo);
            if (response.data.success) {
                toast.success(t('settings.companyInfoUpdated'));
            }
        } catch (error) {
            console.error("Failed to update company info:", error);
            toast.error(t('settings.failedToUpdateCompany'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTax = async () => {
        try {
            setSaving(true);
            const response = await settingsApi.updateTax(taxRates);
            if (response.data.success) {
                toast.success(t('settings.taxSettingsUpdated'));
            }
        } catch (error) {
            console.error("Failed to update tax:", error);
            toast.error(t('settings.failedToUpdateTax'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" fontWeight="bold">{t('settings.systemConfiguration')}</Typography>
                    <Typography variant="body1" color="textSecondary">
                        {t('settings.manageGlobalSettings')}
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={4}>
                {/* Company Information Settings */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.06)', mb: 4 }}>
                        <CardHeader
                            title={t('settings.companyInformation')}
                            subheader={t('settings.manageBranding')}
                            avatar={<InfoIcon color="primary" />}
                            sx={{ pb: 0 }}
                        />
                        <CardContent>
                            <Box component="form" sx={{ mt: 2 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.companyName')}
                                            value={companyInfo.name}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                                            helperText={t('settings.companyNameHelperText')}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.address')}
                                            value={companyInfo.address}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                                            multiline
                                            rows={2}
                                            helperText={t('settings.addressHelperText')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.phoneNumber')}
                                            value={companyInfo.phone}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.email')}
                                            value={companyInfo.email}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.website')}
                                            value={companyInfo.website}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('settings.sloganTagline')}
                                            value={companyInfo.slogan}
                                            onChange={(e) => setCompanyInfo({ ...companyInfo, slogan: e.target.value })}
                                            helperText={t('settings.sloganHelperText')}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                                onClick={handleSaveCompany}
                                                disabled={saving}
                                            >
                                                {saving ? t('settings.saving') : t('settings.saveCompanyInfo')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Tax Settings */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.06)' }}>
                        <CardHeader
                            title={t('settings.taxRegulationSettings')}
                            subheader={t('settings.configureTaxRates')}
                            avatar={<PercentIcon color="primary" />}
                            sx={{ pb: 0 }}
                        />
                        <CardContent>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {t('settings.taxRatesInfo')}
                            </Alert>

                            <Box component="form" sx={{ mt: 2 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>{t('settings.cosmeticsTaxRate')}</Typography>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            value={taxRates.cosmeticsTax}
                                            onChange={(e) => setTaxRates({ ...taxRates, cosmeticsTax: parseFloat(e.target.value) })}
                                            helperText={t('settings.cosmeticsTaxHelperText')}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>{t('settings.generalMedicineTaxRate')}</Typography>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            value={taxRates.generalTax}
                                            onChange={(e) => setTaxRates({ ...taxRates, generalTax: parseFloat(e.target.value) })}
                                            helperText={t('settings.generalTaxHelperText')}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RefreshIcon />}
                                                onClick={fetchSettings}
                                                disabled={saving}
                                            >
                                                {t('settings.reset')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                                onClick={handleSaveTax}
                                                disabled={saving}
                                            >
                                                {saving ? t('settings.saving') : t('settings.saveTaxSettings')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Guide Card */}
                    <Card sx={{
                        mt: 4,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        background: alpha(theme.palette.primary.main, 0.02),
                        border: `1px dashed ${theme.palette.primary.main}`
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
                                <InfoIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, opacity: 0.5 }} />
                                <Typography variant="h6" gutterBottom>{t('settings.configurationGuide')}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t('settings.configGuideDescription')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default SystemSettingsPage;
